import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";
import { createBrowserRouter, Navigate, useRouteError } from "react-router-dom";

import { AdminOnlyRoute, AdminOrOwnerRoute, GuestRoute, ProtectedRoute } from "@/auth/guards";
import { AppLayout } from "@/layouts/AppLayout";
import { DashboardPage } from "@/pages/Dashboard";
import { LoginPage } from "@/pages/Login";
import { NotFoundPage } from "@/pages/NotFound";

// Brocken 24: Route-basiertes Lazy-Loading. Module-Pages, Slideshow,
// Brand-Brief-Pages, Admin-Pages und Workshop-Detail/List werden bei
// Bedarf nachgeladen — die initial sichtbaren Pages (Login, Dashboard,
// NotFound) bleiben im Initial-Bundle, damit der erste Render schnell ist.
//
// Layouts sind ebenfalls lazy, weil sie eigene Sub-Routen tragen und
// die WorkshopLayout-/AdminLayout-Chunks bei Bedarf nachladen.
const WorkshopLayout = lazy(() =>
  import("@/layouts/WorkshopLayout").then((m) => ({ default: m.WorkshopLayout })),
);
const AdminLayout = lazy(() =>
  import("@/layouts/AdminLayout").then((m) => ({ default: m.AdminLayout })),
);

const WorkshopListPage = lazy(() =>
  import("@/pages/workshops/WorkshopList").then((m) => ({ default: m.WorkshopListPage })),
);
const WorkshopCreatePage = lazy(() =>
  import("@/pages/workshops/WorkshopCreate").then((m) => ({ default: m.WorkshopCreatePage })),
);
const WorkshopDetailPage = lazy(() =>
  import("@/pages/workshops/WorkshopDetail").then((m) => ({ default: m.WorkshopDetailPage })),
);
const WorkshopPresentPage = lazy(() =>
  import("@/pages/workshops/WorkshopPresent").then((m) => ({ default: m.WorkshopPresentPage })),
);

const BrandBriefPage = lazy(() =>
  import("@/pages/workshop/BrandBrief").then((m) => ({ default: m.BrandBriefPage })),
);
const BrandBriefListPage = lazy(() =>
  import("@/pages/brand-briefs/BrandBriefList").then((m) => ({ default: m.BrandBriefListPage })),
);
const BrandBriefUploadPage = lazy(() =>
  import("@/pages/brand-briefs/BrandBriefUpload").then((m) => ({
    default: m.BrandBriefUploadPage,
  })),
);
const BrandBriefDetailPage = lazy(() =>
  import("@/pages/brand-briefs/BrandBriefDetail").then((m) => ({
    default: m.BrandBriefDetailPage,
  })),
);
const BrandBriefWizardPage = lazy(() =>
  import("@/pages/brand-briefs/BrandBriefWizard").then((m) => ({
    default: m.BrandBriefWizardPage,
  })),
);

const ModuleArchetypesPage = lazy(() =>
  import("@/pages/modules/ModuleArchetypes").then((m) => ({ default: m.ModuleArchetypesPage })),
);
const ModuleGoldenCirclePage = lazy(() =>
  import("@/pages/modules/ModuleGoldenCircle").then((m) => ({
    default: m.ModuleGoldenCirclePage,
  })),
);
const ModulePersonasPage = lazy(() =>
  import("@/pages/modules/ModulePersonas").then((m) => ({ default: m.ModulePersonasPage })),
);
const ModuleBrandwheelPage = lazy(() =>
  import("@/pages/modules/ModuleBrandwheel").then((m) => ({ default: m.ModuleBrandwheelPage })),
);
const ModuleSalesStoryPage = lazy(() =>
  import("@/pages/modules/ModuleSalesStory").then((m) => ({ default: m.ModuleSalesStoryPage })),
);

const LLMConfigsPage = lazy(() =>
  import("@/pages/admin/LLMConfigs").then((m) => ({ default: m.LLMConfigsPage })),
);
const PromptsPage = lazy(() =>
  import("@/pages/admin/Prompts").then((m) => ({ default: m.PromptsPage })),
);
const CallLogPage = lazy(() =>
  import("@/pages/admin/CallLog").then((m) => ({ default: m.CallLogPage })),
);
const TenantsPage = lazy(() =>
  import("@/pages/admin/Tenants").then((m) => ({ default: m.TenantsPage })),
);
const UsersPage = lazy(() =>
  import("@/pages/admin/Users").then((m) => ({ default: m.UsersPage })),
);
const CustomersPage = lazy(() =>
  import("@/pages/customers/Customers").then((m) => ({ default: m.CustomersPage })),
);

/**
 * Suspense-Fallback während ein Lazy-Chunk geladen wird.
 *
 * Bewusst minimal: ein dezenter Türkis-Punkt mittig. Die Module-Chunks
 * sind klein (<100 KB gzip) — eine Sekunde Spinner würde nervöser wirken
 * als die kurze Ruhe.
 */
function RouteSuspense({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[40vh] items-center justify-center">
          <span className="h-2 w-2 animate-pulse rounded-full bg-primary" aria-hidden />
          <span className="sr-only">Wird geladen …</span>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

const RELOAD_FLAG = "strategify_chunk_reload";

/**
 * Erkennt das typische „stale chunk hash"-Symptom nach einem Deploy
 * (alter Tab fragt eine JS-Datei an, die der neue Build nicht mehr
 * hat) und triggert genau einen automatischen Reload. Verhindert
 * Endlosschleifen über ein `sessionStorage`-Flag — wenn beim Reload
 * der Fehler weiterhin auftritt, ist es ein echter Build-Fehler und
 * der User sieht die Fehlermeldung statt einer Refresh-Schleife.
 */
class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  { errored: boolean }
> {
  state = { errored: false };

  static getDerivedStateFromError(): { errored: boolean } {
    return { errored: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    const message = error.message || "";
    const isChunkError =
      message.includes("dynamically imported module") ||
      message.includes("Loading chunk") ||
      message.includes("Failed to fetch");
    if (!isChunkError) return;

    if (sessionStorage.getItem(RELOAD_FLAG) === "1") {
      // Schon einmal reloadet — vermutlich echter Build-Fehler, nicht reloaden
      return;
    }
    sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
  }

  render(): ReactNode {
    if (this.state.errored && sessionStorage.getItem(RELOAD_FLAG) === "1") {
      return (
        <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 p-6 text-center">
          <p className="text-sm text-muted-foreground">
            Diese Seite konnte nach dem letzten Update nicht aktualisiert werden.
          </p>
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem(RELOAD_FLAG);
              window.location.reload();
            }}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Neu laden
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// Erfolgreich gerenderter Route-Wechsel? Flag zurücksetzen, damit der
// nächste echte stale-chunk-Vorfall den Reload wieder auslösen darf.
if (typeof window !== "undefined") {
  window.addEventListener("load", () => {
    sessionStorage.removeItem(RELOAD_FLAG);
  });
}

// Helper, damit der Suspense-Wrapper nicht in jeder Route-Zeile wiederholt
// werden muss.
const lazyElement = (element: ReactNode): ReactNode => (
  <ChunkErrorBoundary>
    <RouteSuspense>{element}</RouteSuspense>
  </ChunkErrorBoundary>
);

/**
 * Brocken 38 — react-router-eigener Error-Pfad für Lazy-Chunk-Fehler.
 *
 * Hintergrund: react-router 6.4+ fängt Errors innerhalb seiner Routing-
 * Logik (auch beim Lazy-Loading von Route-Elements) **selbst** ab, bevor
 * sie eine React-Tree-ErrorBoundary erreichen. Default-Fallback ist die
 * berüchtigte „Unexpected Application Error!"-Seite — die wir bei jedem
 * Deploy zu sehen bekommen, weil der Browser-Cache noch auf alte
 * Chunk-Hashes zeigt.
 *
 * Diese Komponente sitzt als `errorElement` auf der Root-Route, prüft
 * den Error via `useRouteError`, und triggert für stale-chunk-Symptome
 * exakt einen automatischen Reload — analog zu ChunkErrorBoundary, aber
 * im router-eigenen Error-Pfad.
 */
function RouterErrorElement(): ReactNode {
  const error = useRouteError() as { message?: string } | undefined;
  const message =
    (typeof error === "object" && error && "message" in error
      ? String(error.message ?? "")
      : "") || "";
  const isChunkError =
    message.includes("dynamically imported module") ||
    message.includes("Loading chunk") ||
    message.includes("Failed to fetch") ||
    // Vite + Chrome werfen manchmal nur „Importing a module script failed":
    message.includes("Importing a module script failed");

  if (isChunkError && sessionStorage.getItem(RELOAD_FLAG) !== "1") {
    sessionStorage.setItem(RELOAD_FLAG, "1");
    window.location.reload();
    return null;
  }

  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
      <p className="text-sm text-muted-foreground">
        {isChunkError
          ? "Diese Seite konnte nach dem letzten Update nicht aktualisiert werden."
          : "Etwas ist schiefgelaufen."}
      </p>
      <button
        type="button"
        onClick={() => {
          sessionStorage.removeItem(RELOAD_FLAG);
          window.location.reload();
        }}
        className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
      >
        Neu laden
      </button>
    </div>
  );
}

export const router = createBrowserRouter([
  {
    element: <AppLayout />,
    errorElement: <RouterErrorElement />,
    children: [
      {
        element: <GuestRoute />,
        children: [{ path: "/login", element: <LoginPage /> }],
      },
      {
        element: <ProtectedRoute />,
        children: [
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/workshops", element: lazyElement(<WorkshopListPage />) },
          { path: "/workshops/new", element: lazyElement(<WorkshopCreatePage />) },
          { path: "/workshops/brand-brief", element: lazyElement(<BrandBriefPage />) },
          {
            path: "/workshops/:id",
            element: lazyElement(<WorkshopLayout />),
            children: [
              { index: true, element: lazyElement(<WorkshopDetailPage />) },
              { path: "brand-brief", element: lazyElement(<BrandBriefPage />) },
              { path: "modules/archetypes", element: lazyElement(<ModuleArchetypesPage />) },
              {
                path: "modules/golden-circle",
                element: lazyElement(<ModuleGoldenCirclePage />),
              },
              { path: "modules/personas", element: lazyElement(<ModulePersonasPage />) },
              { path: "modules/brandwheel", element: lazyElement(<ModuleBrandwheelPage />) },
              { path: "modules/sales-story", element: lazyElement(<ModuleSalesStoryPage />) },
            ],
          },
          // Vollbild-Slideshow — bewusst außerhalb des WorkshopLayout,
          // damit kein Workshop-Stepper über der Bühne hängt.
          { path: "/workshops/:id/present", element: lazyElement(<WorkshopPresentPage />) },
          { path: "/brand-briefs", element: lazyElement(<BrandBriefListPage />) },
          { path: "/brand-briefs/upload", element: lazyElement(<BrandBriefUploadPage />) },
          { path: "/brand-briefs/wizard", element: lazyElement(<BrandBriefWizardPage />) },
          { path: "/brand-briefs/:id", element: lazyElement(<BrandBriefDetailPage />) },
        ],
      },
      {
        element: <AdminOrOwnerRoute />,
        children: [
          { path: "/customers", element: lazyElement(<CustomersPage />) },
          {
            element: lazyElement(<AdminLayout />),
            children: [
              { path: "/admin", element: <Navigate to="/admin/llm-configs" replace /> },
              { path: "/admin/llm-configs", element: lazyElement(<LLMConfigsPage />) },
              { path: "/admin/prompts", element: lazyElement(<PromptsPage />) },
              { path: "/admin/call-log", element: lazyElement(<CallLogPage />) },
              // Tenant- + User-Management nur für Operator-Admins:
              // Eigener Guard innerhalb des AdminLayouts, damit die Sidebar
              // sichtbar bleibt, wenn ein Owner irrtümlich /admin/users öffnet.
              {
                element: <AdminOnlyRoute />,
                children: [
                  { path: "/admin/tenants", element: lazyElement(<TenantsPage />) },
                  { path: "/admin/users", element: lazyElement(<UsersPage />) },
                ],
              },
            ],
          },
        ],
      },
      { path: "/", element: <Navigate to="/dashboard" replace /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
